import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cn } from 'lib/utils'
import * as React from 'react'

const Tabs = TabsPrimitive.Root

/**
 * Two T3 tab patterns (see playbook/design-system/components.md#tabs):
 *
 * - `pill` — compact segmented control (`.t3-segment`). Acid active pill
 *   inside a bordered tray. Use for filters and section switches.
 *
 * - `dark` — context strip. Paper-dim labels, white hover, acid underline
 *   on active. Use for in-workspace switching (composer platform tabs).
 *
 * - `default` — legacy underline tabs (purple primary). Avoid in new code.
 *
 * Active/hover colors live in design-system.css (`.t3-segment-tab`,
 * `.t3-tab-context`) because T3 token classes (`.bg-acid`, `.text-paper`) are plain CSS, not
 * Tailwind utilities — variant-prefixed Tailwind rules on them silently no-op.
 */

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    variant?: 'default' | 'dark' | 'pill'
  }
>(({ className, variant = 'default', ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    data-variant={variant}
    className={cn(
      variant === 'default' &&
        'inline-flex h-10 items-center justify-center rounded-md p-1 text-muted-foreground',
      variant === 'dark' && 't3-tabs-context',
      variant === 'pill' && 't3-segment',
      className
    )}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    variant?: 'default' | 'dark' | 'pill'
  }
>(({ className, variant = 'default', ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      variant === 'default' && [
        'relative inline-flex cursor-pointer items-center justify-center px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-background transition-all',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
        'after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-full after:origin-left after:scale-x-0 after:bg-primary after:transition-transform after:duration-300',
        'data-[state=active]:text-foreground data-[state=active]:after:scale-x-100',
        'data-[state=inactive]:after:origin-right',
        'disabled:pointer-events-none disabled:opacity-50',
      ],
      variant === 'dark' && 't3-tab-context',
      variant === 'pill' && 't3-segment-tab',
      className
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsContent, TabsList, TabsTrigger }
